import {
  ActionButton,
  CalendarHeader,
  Paginator,
  TableComponent,
  YearHeader,
} from "components";
import { extractVehicleNumberFromDetails, withPermissions } from "utils";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import FilterMenu from "./filter";
import ReinstateContainer from "./reopen";
import clsx from "clsx";
import numeral from "numeral";
import { usePagination } from "hooks";
import { useReinstatementOffers } from "hooks/data/use-reinstatement-offers";

const ReinstatementOffersPage = () => {
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage } = usePagination();

  const { offers, count, refetch, loading } = useReinstatementOffers({
    filter: {
      offset: offset,
      limit: limit,
      insurer_id: searchParams.insurer as string[],
      payment_status: searchParams?.payment_status
        ? [searchParams.payment_status]
        : [],
      expiry_status: searchParams?.expiry_status,
      offer_from: searchParams?.offer_from,
      class_of_business_id: searchParams?.business as string[],
      search: (searchParams.search || "").toString(),
      year: searchParams.year?.toString() || undefined,
    },
  });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <YearHeader />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            hasSearch
            title={"Banking Tray"}
            loading={loading}
            columns={[
              {
                name: "Policy #",
                accessor: "offer_detail.policy_number",
                render: (value) => (
                  <div className="flex flex-col items-start flex-wrap break-all break-words">
                    <div className="flex flex-row items-center gap-x-1">
                      <span className="font-bold text-xs overflow-hidden text-ellipsis  ">
                        {value.offer_detail_reinstate?.policy_number || "N/A"}
                      </span>
                    </div>
                    <div
                      className={clsx(
                        "p-0.5 rounded-full",
                        {
                          " text-green-600": value.offer_from === "INTERNAL",
                          " text-yellow-600": value.offer_from !== "INTERNAL",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.offer_from}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Reinsured",
                accessor: "insurer.insurer_company_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.insurer?.insurer_company_name || "N/A"}
                    </span>
                    <span className="font-light">
                      {value.insurer?.insurer_company_email || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Insured",
                accessor: "offer_detail.insured_by",
                render: (value) => {
                  const details = JSON.parse(
                    value?.offer_detail_reinstate?.offer_details || "[]"
                  );
                  return (
                    <div className="flex flex-col text-xs ">
                      <span className="font-medium">
                        {value.offer_detail_reinstate?.insured_by || "N/A"}
                      </span>
                      <span className="font-light">
                        {[
                          "Motor Comprehensive Fleet",
                          "Motor Comprehensive",
                          "Motor Comprehensive (Automobile Fac Facility)",
                        ].includes(value?.classofbusiness?.business_name || "")
                          ? extractVehicleNumberFromDetails(details)
                          : ""}
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "Class Of Business",
                accessor: "classofbusiness.business_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.classofbusiness?.business_name || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Sum Insured",
                accessor: "sum_insured",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail_reinstate?.currency}{" "}
                      {numeral(value.sum_insured).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Premium",
                accessor: "premium",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail_reinstate?.currency}{" "}
                      {numeral(value.premium).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Offer Date",
                accessor: "created_at",
                render: (value) =>
                  new Date(value.created_at).toLocaleDateString(),
              },
              {
                name: "Expiry Status",
                accessor: "expiry_status",
                render: (value) => (
                  <div className="flex flex-col items-start">
                    <div
                      className={clsx(
                        "p-1 rounded-lg",
                        {
                          "bg-green-600/30 text-green-600":
                            value.expiry_status === "ACTIVE",
                          "bg-red-50/30 text-red-600":
                            value.expiry_status === "EXPIRED",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.expiry_status}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Approval Status",
                accessor: "approval_status",
                render: (value) => (
                  <div className="flex">
                    <div
                      className={clsx(
                        "p-1 rounded-lg",
                        {
                          "bg-green-600/30 text-green-600":
                            value.approval_status === "APPROVED",
                          "bg-red-300/30 text-red-600":
                            value.approval_status === "UNAPPROVED",
                          "bg-sky-600/30 text-sky-600":
                            value.approval_status === "MODIFY",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.approval_status}</span>
                    </div>
                  </div>
                ),
              },
              {
                accessor: "offer_detail_reinstate.payment_type",
                name: "Payment Status",
                render: (value) => (
                  <div className="flex">
                    <div
                      className={clsx(
                        "p-1 rounded-lg",
                        {
                          "bg-green-600/30 text-green-600":
                            value.payment_status === "PAID",
                          "bg-red-300/30 text-red-600":
                            value.payment_status === "UNPAID",
                          "bg-sky-600/30 text-sky-600":
                            value.payment_status === "PARTPAYMENT",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.payment_status}</span>
                    </div>
                  </div>
                ),
              },

              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:reinstate",
                    ])(
                      <ActionButton
                        action="reject"
                        tooltip="Reinstate Offer"
                        onClick={dispatchAction(value?.offer_id, "reject")}
                      />
                    )}
                  </>
                ),
              },
            ]}
            data={offers}
            refetch={refetch}
            pagination={() => (
              <Paginator
                currentSize={offers.length}
                offset={offset}
                limit={limit}
                setOffset={setPage}
                totalItems={count}
              />
            )}
            renderFilter={({ filterOpen, setFilterOpen }) => (
              <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
            )}
          />
        </div>
      </div>
      {searchParams?.id && searchParams?.modal === "reject" && (
        <ReinstateContainer
          open={searchParams?.modal === "reject"}
          setOpen={(val) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "reject" : undefined,
                  id: val ? prev?.id : undefined,
                };
              },
            })
          }
          refetch={refetch}
        />
      )}
    </main>
  );
};

export default ReinstatementOffersPage;
